import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

import ProgressiveButton from 'views/pages/ProgressiveButton'
import Search from 'views/pages/Search'
import Comment from './Comment'
import AssignTabs from 'views/pages/AssignTabs'
import File from 'views/pages/File'
import ThumbnailInput from './ThumbnailInput'
import ToggleRow from './ToggleRow'
import TextEditor from '../../TextEditor'
import {
  resetSelectedItems,
  setPublic,
  setAssignSearchValue,
  setCurrentTab,
} from 'store/Library/actions'
import { useDispatch } from 'react-redux'
import {
  Input,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  Button,
  FormGroup,
  Label,
  Badge,
  ListGroup,
  ListGroupItem,
  Alert,
} from 'reactstrap'
import CustomModal from 'views/pages/CustomModal'
import { upload, uploadMultiple, uploadNote } from 'store/Library/actions'
import Select from 'views/pages/Select'
import { useSelector } from 'react-redux'
import { selectFolder, syncSelectedFolder } from 'store/Library/actions'
import { setAreYouSureModalData } from 'store/Recruitment/actions'

const Upload = (props) => {
  const { enableLanguageFiles } = useSelector((store) => store.generalConfig)
  const [fileLanguages, setFileLanguages] = useState([
    { name: 'English', value: 1 },
    { name: 'Spanish', value: 3 },
  ])
  const language = props.language || {}
  // Upload mode states
  const [uploadMode, setUploadMode] = useState('single') // 'single', 'multiple', 'note'
  const [fileQueue, setFileQueue] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState('')
  const [selectedTargetFolder, setSelectedTargetFolder] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)

  // Note mode states
  const [noteTitle, setNoteTitle] = useState('')
  const [noteContent, setNoteContent] = useState('')

  // Refs
  const mainFileInputRef = useRef(null)
  const singleThumbnailRef = useRef(null)
  const noteThumbnailRef = useRef(null)

  const dispatch = useDispatch()
  const [activeFolder, setActiveFolder] = useState(
    props.library?.parent?.name || 'Select Folder',
  )

  const { enableCategories } = props.courseConfig
  const { allFolders, allFoldersNested } = props.library
  const defaultObj = {
    name: 'Select Folder',
    id: null,
  }
  const rootDirectory = {
    id: null,
    name: 'Library',
    parent_id: null,
  }
  const foldersNested = [defaultObj, rootDirectory, ...allFoldersNested]

  const handleFolderChange = (event) => {
    const folder = allFolders.find((folder) => folder.id === event.option.value)

    if (!folder) {
      dispatch(selectFolder(null))
    } else {
      dispatch(selectFolder(folder.id))
    }
  }

  useEffect(() => {
    return () => {
      dispatch(setPublic('public'))
      dispatch(resetSelectedItems())
      dispatch(
        setCurrentTab(
          'category',
          'selectedOrgGroups',
          'groupOrgs',
          'toggleOrgGroups',
          'showSelectedOrgGroups',
          'setShowSelectedOrgGroups',
          'setHideSelectedOrgGroups',
        ),
      )
      dispatch(setAssignSearchValue(''))
    }
  }, [dispatch])
  useEffect(() => {
    // Start from the folder we're currently viewing (root when there is none), so
    // the selection can never be left over from a folder visited earlier. This only
    // re-runs when the viewed folder changes, so manual picks in the dropdown stay.
    dispatch(syncSelectedFolder())

    // Closing the modal (even without saving) puts the selection back as well.
    return () => {
      dispatch(syncSelectedFolder())
    }
  }, [dispatch, props.library?.parentId])

  // Separate useEffect for updating target folder for multiple upload
  useEffect(() => {
    // The store's selection is the single source of truth: null / 'lib' / '' = root.
    const selected = props.library?.selectedFolder
    setSelectedTargetFolder(
      selected === 'lib' || selected === '' || selected === undefined
        ? null
        : selected,
    )
  }, [props.library?.selectedFolder])

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (uploadSuccess) {
      const timer = setTimeout(() => {
        setUploadSuccess('')
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [uploadSuccess])

  // Cleanup object URLs on component unmount
  useEffect(() => {
    return () => {
      // Clean up all thumbnail preview URLs when component unmounts
      fileQueue.forEach((file) => {
        if (file.thumbnailPreview) {
          URL.revokeObjectURL(file.thumbnailPreview)
        }
      })
    }
  }, [fileQueue])

  const flattenFolders = (folders, prefix = '') => {
    let flatList = []
    folders.forEach((item) => {
      flatList.push({ ...item, label: `${prefix}${item.name}` })
      if (item.children && item.children.length > 0) {
        flatList = flatList.concat(flattenFolders(item.children, `${prefix}--`))
      }
    })
    return flatList
  }

  // Flatten the nested folders structure with prefix
  const flattenedFolders = flattenFolders(foldersNested)

  // Generate options for react-select
  const selectOptions = flattenedFolders
    .filter(
      (item) =>
        item.name !== 'Library' &&
        item.name !== 'Select Folder' &&
        props.match.params.id !== item.id,
    )
    .map((item) => ({ value: item.id, name: item.label }))

  const selectedFolderInitial = selectOptions.find(
    (option) => option.value === props.library.selectedFolder,
  )

  const selectedFolder = !selectedFolderInitial
    ? { name: 'Library', id: 'lib' }
    : selectedFolderInitial

  // Multiple file upload functions
  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files)

    // Clear any previous errors
    setUploadError('')
    setUploadSuccess('')

    if (files.length === 0) return

    // Check for file size limits (example: 100MB per file)
    const oversizedFiles = files.filter((file) => file.size > 100 * 1024 * 1024)
    if (oversizedFiles.length > 0) {
      setUploadError(
        `Some files are too large. Maximum file size is 100MB. Files: ${oversizedFiles
          .map((f) => f.name)
          .join(', ')}`,
      )
      return
    }

    // Check for duplicate files based on name and size
    const duplicateFiles = files.filter((file) =>
      fileQueue.some(
        (queueFile) =>
          queueFile.file.name === file.name &&
          queueFile.file.size === file.size,
      ),
    )

    if (duplicateFiles.length > 0) {
      setUploadError(
        `Some files are already in the queue: ${duplicateFiles
          .map((f) => f.name)
          .join(', ')}`,
      )
      return
    }

    const newFiles = files.map((file, index) => ({
      id: Date.now() + index,
      file: file,
      name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      thumbnail: null,
      path: '',
      newTab: true,
    }))

    setFileQueue((prev) => [...prev, ...newFiles])

    // Reset the main file input
    if (mainFileInputRef.current) {
      mainFileInputRef.current.value = ''
    }

    // Show success message for file selection
    setUploadSuccess(`Added ${files.length} file(s) to upload queue`)
  }

  const removeFileFromQueue = (fileId) => {
    setFileQueue((prev) => {
      const fileToRemove = prev.find((file) => file.id === fileId)
      // Clean up thumbnail preview URL to prevent memory leaks
      if (fileToRemove?.thumbnailPreview) {
        URL.revokeObjectURL(fileToRemove.thumbnailPreview)
      }
      return prev.filter((file) => file.id !== fileId)
    })
  }

  const clearAllFiles = () => {
    if (fileQueue.length > 0) {
      dispatch(
        setAreYouSureModalData({
          show: true,
          hideDefault: true,
          text: `Are you sure you want to remove all ${fileQueue.length} files from the queue?`,
          customFn: () => {
            // Clean up all thumbnail preview URLs
            fileQueue.forEach((file) => {
              if (file.thumbnailPreview) {
                URL.revokeObjectURL(file.thumbnailPreview)
              }
            })
            setFileQueue([])
            setUploadSuccess('All files cleared from queue')
          },
        }),
      )
    }
  }

  const updateFileName = (fileId, newName) => {
    setFileQueue((prev) =>
      prev.map((file) =>
        file.id === fileId ? { ...file, name: newName } : file,
      ),
    )
  }

  const updateFileThumbnail = (fileId, thumbnail) => {
    setFileQueue((prev) =>
      prev.map((file) => {
        if (file.id === fileId) {
          // Create preview URL for the thumbnail
          const thumbnailPreview = thumbnail
            ? URL.createObjectURL(thumbnail)
            : null
          return { ...file, thumbnail, thumbnailPreview }
        }
        return file
      }),
    )
  }

  const updateFileNewTab = (fileId, newTab) => {
    setFileQueue((prev) =>
      prev.map((file) => (file.id === fileId ? { ...file, newTab } : file)),
    )
  }

  const updateFileReplacement = (fileId, newFile) => {
    setFileQueue((prev) =>
      prev.map((file) =>
        file.id === fileId
          ? {
              ...file,
              file: newFile,
              name: newFile.name.replace(/\.[^/.]+$/, ''), // Update name to match new file
            }
          : file,
      ),
    )
  }

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      // Create a fake event to use the existing handleFileSelection logic
      const fakeEvent = {
        target: {
          files: files,
        },
      }
      handleFileSelection(fakeEvent)
    }
  }

  const handleMultipleUpload = () => {
    if (fileQueue.length === 0) {
      setUploadError('Please select files to upload')
      return
    }

    if (enableLanguageFiles && (!fileLanguages || !fileLanguages?.length)) {
      setUploadError('Please select a language!')
      return
    }

    setIsUploading(true)
    setUploadError('')
    setUploadSuccess('')

    dispatch(
      uploadMultiple(
        fileQueue,
        { targetFolder: selectedTargetFolder },
        fileLanguages,
      ),
    )
      .then((response) => {
        setIsUploading(false)
        setFileQueue([])
        setUploadMode('single')

        // Show success message briefly before closing modal
        const scopeText =
          props.library.scope === 'public' ? 'public' : 'private'
        const languageText =
          enableLanguageFiles && fileLanguages && fileLanguages.length > 0
            ? ` with ${fileLanguages
                .map((lang) => lang.name)
                .join(', ')} language settings`
            : ''

        setUploadSuccess(
          `Successfully uploaded ${fileQueue.length} files as ${scopeText}${languageText}!`,
        )

        // Close modal after successful upload (delay for user to see success message)
        setTimeout(() => {
          props.history.push(
            props.library.parentId
              ? '/library/folder/' + props.library.parentId
              : '/library',
          )
        }, 1500)
      })
      .catch((error) => {
        setIsUploading(false)
        setUploadError(
          error.message || 'Failed to upload files. Please try again.',
        )
      })
  }

  return (
    <CustomModal
      backPath={
        props.library.parentId
          ? '/library/folder/' + props.library.parentId
          : '/library'
      }
      title={
        uploadMode === 'multiple'
          ? 'Upload Multiple Files'
          : uploadMode === 'note'
            ? 'Add Note'
            : props.title
              ? props.title
              : 'Upload file'
      }
      size={'xl'}
      className={'modal-95vh'}
    >
      <ModalBody
        className={`scrollbar modal-body-95vh`}
        style={{ maxHeight: '85vh', overflowY: 'auto', height: 'auto' }}
      >
        {/* Success/Error Messages */}
        {uploadSuccess && (
          <Alert
            color='success'
            className='mb-3'
            isOpen={!!uploadSuccess}
            toggle={() => setUploadSuccess('')}
          >
            <i className='fa fa-check-circle' /> {uploadSuccess}
          </Alert>
        )}
        {uploadError && (
          <Alert
            color='danger'
            className='mb-3'
            isOpen={!!uploadError}
            toggle={() => setUploadError('')}
          >
            <i className='fa fa-exclamation-triangle' /> {uploadError}
          </Alert>
        )}

        {/* Upload Mode Switcher */}
        {props.chooseFile !== false ? (
          <div className='row mb-4'>
            <div className='col-12'>
              <div className='d-flex justify-content-center'>
                <div
                  className='btn-group'
                  role='group'
                  aria-label='Upload mode'
                >
                  <button
                    type='button'
                    className={`btn ${
                      uploadMode === 'single'
                        ? 'btn-primary'
                        : 'btn-outline-primary'
                    }`}
                    onClick={() => {
                      setUploadMode('single')
                      setFileQueue([])
                      setNoteTitle('')
                      setNoteContent('')
                      setUploadError('')
                      setUploadSuccess('')
                    }}
                  >
                    📁 Single File
                  </button>
                  <button
                    type='button'
                    className={`btn ${
                      uploadMode === 'multiple'
                        ? 'btn-primary'
                        : 'btn-outline-primary'
                    }`}
                    onClick={() => {
                      setUploadMode('multiple')
                      setNoteTitle('')
                      setNoteContent('')
                      setUploadError('')
                      setUploadSuccess('')
                    }}
                  >
                    📚 Multiple Files
                  </button>
                  <button
                    type='button'
                    className={`btn ${
                      uploadMode === 'note'
                        ? 'btn-primary'
                        : 'btn-outline-primary'
                    }`}
                    onClick={() => {
                      setUploadMode('note')
                      setFileQueue([])
                      setUploadError('')
                      setUploadSuccess('')
                    }}
                  >
                    📝 Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Multiple Files Interface */}
        {uploadMode === 'multiple' ? (
          <div>
            {/* Folder Selection for Multiple Upload */}
            <div className='row mb-3'>
              <div className='col-12'>
                <Label className='form-control-label font-weight-bold'>
                  Upload to Folder
                </Label>
                <Select
                  options={[{ name: 'Library', id: 'lib' }, ...selectOptions]}
                  onChange={(e) => {
                    // Fix: Handle root folder selection properly
                    const folderId =
                      e.option.value === 'lib' ? null : e.option.value
                    setSelectedTargetFolder(folderId)
                  }}
                  language={language}
                  placeholder={
                    selectedTargetFolder === null
                      ? 'Library'
                      : selectOptions.find(
                          (opt) => opt.value === selectedTargetFolder,
                        )?.name || 'Library'
                  }
                  multi={false}
                  selectedOption={
                    selectedTargetFolder === null
                      ? { name: 'Library', id: null }
                      : selectOptions.find(
                          (opt) => opt.value === selectedTargetFolder,
                        ) || { name: 'Library', id: null }
                  }
                />
              </div>
            </div>

            {/* Language Settings */}
            {enableLanguageFiles && (
              <div className='row mb-3'>
                <div className='col-12'>
                  <Label className='form-control-label font-weight-bold'>
                    Language Settings (applies to all files)
                  </Label>
                  <Select
                    options={[
                      { name: 'English', value: 1 },
                      { name: 'Spanish', value: 3 },
                    ]}
                    onChange={(selected) => {
                      setFileLanguages(selected)
                    }}
                    language={language}
                    placeholder={'Select Language'}
                    multi={true}
                    selectedOptions={fileLanguages}
                  />
                </div>
              </div>
            )}

            {/* Privacy Settings */}
            <div className='row mb-3'>
              <div className='col-12'>
                <Label className='form-control-label font-weight-bold'>
                  Privacy Settings (applies to all files)
                </Label>
                <div className='d-flex align-items-center mt-2'>
                  <div className='custom-control custom-radio mr-4'>
                    <input
                      className='custom-control-input'
                      type='radio'
                      name='multipleFileScope'
                      id='multiple-set-public'
                      checked={props.library.scope === 'public'}
                      onChange={() => dispatch(setPublic('public'))}
                    />
                    <label
                      className='custom-control-label'
                      htmlFor='multiple-set-public'
                    >
                      {language.my_library
                        ? language.my_library.public
                        : 'Public'}
                    </label>
                  </div>

                  <div className='custom-control custom-radio'>
                    <input
                      className='custom-control-input'
                      type='radio'
                      name='multipleFileScope'
                      id='multiple-set-private'
                      checked={props.library.scope === 'private'}
                      onChange={() => dispatch(setPublic('private'))}
                    />
                    <label
                      className='custom-control-label'
                      htmlFor='multiple-set-private'
                    >
                      {language.my_library
                        ? language.my_library.private
                        : 'Private'}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Assignment Interface for Private Files - Multiple Upload Mode */}
            {props.library.scope === 'private' && (
              <div className='row mb-4'>
                <div className='col-12'>
                  <div className='alert alert-info border-left-primary mb-3'>
                    <i className='fa fa-info-circle mr-2' />
                    <strong>Assignment Settings:</strong> The assignment
                    settings below will apply to all files in your upload queue.
                  </div>
                  <div className='d-flex mt-3 mb-2'>
                    <Nav
                      className='nav-fill flex-column flex-sm-row col-12 p-2 rounded'
                      id='tabs-text'
                      style={{ backgroundColor: '#f6f9fc' }}
                      pills
                      role='tablist'
                    >
                      <NavItem className='nav-items'>
                        <NavLink
                          aria-selected={
                            props.library.currentTab === 'category'
                          }
                          className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                            props.library.currentTab === 'category'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                          onClick={() =>
                            dispatch(
                              setCurrentTab(
                                'category',
                                'selectedOrgGroups',
                                'groupOrgs',
                                'toggleOrgGroups',
                                'showSelectedOrgGroups',
                                'setShowSelectedOrgGroups',
                                'setHideSelectedOrgGroups',
                              ),
                            )
                          }
                          href='#pablo'
                          role='tab'
                        >
                          {language.general
                            ? language.general.category
                            : 'Category'}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          aria-selected={props.library.currentTab === 'user'}
                          className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                            props.library.currentTab === 'user'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                          onClick={() =>
                            dispatch(
                              setCurrentTab(
                                'user',
                                'selectedUsers',
                                'users',
                                'toggleUsers',
                                'showSelectedUsers',
                                'setShowSelectedUsers',
                                'setHideSelectedUsers',
                              ),
                            )
                          }
                          href='#pablo'
                          role='tab'
                          color='none'
                        >
                          {language.general ? language.general.users : 'Users'}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          aria-selected={
                            props.library.currentTab === 'location-group'
                          }
                          className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                            props.library.currentTab === 'location-group'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                          onClick={() =>
                            dispatch(
                              setCurrentTab(
                                'location-group',
                                'selectedLocationGroups',
                                'locationGroups',
                                'toggleLocationGroups',
                                'showSelectedLocationGroups',
                                'setShowSelectedLocationGroups',
                                'setHideSelectedLocationGroups',
                              ),
                            )
                          }
                          href='#pablo'
                          role='tab'
                        >
                          {language.general
                            ? language.general.location_categories
                            : 'Location Groups'}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          aria-selected={props.library.currentTab === 'stores'}
                          className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                            props.library.currentTab === 'stores'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                          onClick={() =>
                            dispatch(
                              setCurrentTab(
                                'stores',
                                'selectedStores',
                                'stores',
                                'toggleStores',
                                'showSelectedStores',
                                'setShowSelectedStores',
                                'setHideSelectedStores',
                              ),
                            )
                          }
                          href='#pablo'
                          role='tab'
                        >
                          {language.settings
                            ? language.settings.location
                            : 'Location'}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          aria-selected={
                            props.library.currentTab === 'job-title'
                          }
                          className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                            props.library.currentTab === 'job-title'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                          onClick={() =>
                            dispatch(
                              setCurrentTab(
                                'job-title',
                                'selectedRoles',
                                'roles',
                                'toggleRoles',
                                'showSelectedRoles',
                                'setShowSelectedRoles',
                                'setHideSelectedRoles',
                              ),
                            )
                          }
                          href='#pablo'
                          role='tab'
                        >
                          {language.settings
                            ? language.settings.assign_job_title
                            : 'Job Title'}
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>

                  <AssignTabs
                    items={props[props.library.currentItems]}
                    toggleItem={props[props.library.currentToggleFunction]}
                    selectedItems={props.library.currentSelectedItems}
                    mainProps={props.library}
                    assignSearchValue={props.library.assignSearchValue}
                    setHideSelectedItems={
                      props[props.library.currentHideSelectedItemsFunction]
                    }
                    setShowSelectedItems={
                      props[props.library.currentShowSelectedItemsFunction]
                    }
                    showSelectedItems={
                      props.library[props.library.currentShowSelectedItems]
                    }
                    language={props.language}
                    resetSelectedItems={() => dispatch(resetSelectedItems())}
                    setCurrentTab={setCurrentTab}
                    setAssignSearchValue={(event) =>
                      dispatch(setAssignSearchValue(event.target.value))
                    }
                    setPublic={(val) => dispatch(setPublic(val))}
                    enableCategories={enableCategories}
                  />
                </div>
              </div>
            )}

            {/* File Selection with Drag & Drop */}
            <div className='row mb-3'>
              <div className='col-12'>
                <Label className='form-control-label font-weight-bold'>
                  Select Files
                </Label>
                <div
                  className={`border border-2 rounded p-4 text-center ${
                    isDragOver
                      ? 'border-primary bg-light'
                      : 'border-dashed border-secondary'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => mainFileInputRef.current?.click()}
                >
                  <div className='mb-2'>
                    <i
                      className={`fa fa-cloud-upload fa-2x ${
                        isDragOver ? 'text-primary' : 'text-muted'
                      }`}
                    />
                  </div>
                  <div className='mb-2'>
                    <strong
                      className={isDragOver ? 'text-primary' : 'text-dark'}
                    >
                      {isDragOver
                        ? 'Drop files here!'
                        : 'Drag & drop files here'}
                    </strong>
                  </div>
                  <div className='text-muted'>or click to browse</div>
                  <Input
                    type='file'
                    multiple
                    onChange={handleFileSelection}
                    accept='*'
                    className='d-none'
                    innerRef={mainFileInputRef}
                  />
                </div>
              </div>
            </div>

            {/* Current Settings Summary */}
            {fileQueue.length > 0 && (
              <div className='row mb-2'>
                <div className='col-12'>
                  <div className='card border-light bg-light'>
                    <div className='card-body py-2 px-3'>
                      <div className='d-flex align-items-center justify-content-between'>
                        <div className='d-flex align-items-center'>
                          <i className='fa fa-cog text-muted mr-2' />
                          <small className='text-muted font-weight-bold'>
                            Applied to all files:
                          </small>
                        </div>
                        <div className='d-flex align-items-center'>
                          <Badge
                            color={
                              props.library.scope === 'public'
                                ? 'success'
                                : 'warning'
                            }
                            className='mr-2'
                          >
                            <i
                              className={`fa ${
                                props.library.scope === 'public'
                                  ? 'fa-globe'
                                  : 'fa-lock'
                              } mr-1`}
                            />
                            {props.library.scope === 'public'
                              ? 'Public'
                              : 'Private'}
                          </Badge>
                          {enableLanguageFiles &&
                            fileLanguages &&
                            fileLanguages.length > 0 && (
                              <Badge color='primary'>
                                <i className='fa fa-language mr-1' />
                                {fileLanguages
                                  .map((lang) => lang.name)
                                  .join(', ')}
                              </Badge>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* File Queue */}
            {fileQueue.length > 0 && (
              <div className='row mb-3'>
                <div className='col-12'>
                  <div className='d-flex align-items-center justify-content-between mb-2'>
                    <Label className='form-control-label font-weight-bold mb-0'>
                      Files to Upload{' '}
                      <Badge color='info' pill>
                        {fileQueue.length}
                      </Badge>
                    </Label>
                    <Button
                      size='sm'
                      color='danger'
                      outline
                      onClick={clearAllFiles}
                      title='Clear all files from queue'
                    >
                      <i className='fa fa-trash mr-1' />
                      Clear All
                    </Button>
                  </div>
                  <ListGroup
                    flush
                    // style={{ maxHeight: '300px', overflowY: 'auto' }}
                  >
                    {fileQueue.map((queueFile, index) => (
                      <ListGroupItem key={queueFile.id} className='px-0'>
                        <div className='d-flex align-items-center justify-content-between mb-2'>
                          <Badge color='secondary' pill>
                            File {index + 1}
                          </Badge>
                          <Button
                            size='sm'
                            color='danger'
                            outline
                            onClick={() => removeFileFromQueue(queueFile.id)}
                          >
                            <i className='fa fa-times' />
                          </Button>
                        </div>
                        <div className='row mb-2'>
                          <div className='col-12'>
                            <Label className='form-control-label small'>
                              File Name
                            </Label>
                            <Input
                              type='text'
                              placeholder='File name'
                              value={queueFile.name}
                              onChange={(e) =>
                                updateFileName(queueFile.id, e.target.value)
                              }
                              size='sm'
                            />
                          </div>
                        </div>
                        <div className='row'>
                          <div className='col-6'>
                            <Label className='form-control-label small'>
                              Replace File
                            </Label>
                            <Input
                              type='file'
                              accept='*'
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  updateFileReplacement(
                                    queueFile.id,
                                    e.target.files[0],
                                  )
                                }
                              }}
                              size='sm'
                            />
                          </div>
                          <div className='col-6'>
                            <Label className='form-control-label small'>
                              Thumbnail
                            </Label>
                            <ThumbnailInput
                              placeholder='Choose thumbnails'
                              onChange={(file) =>
                                updateFileThumbnail(queueFile.id, file)
                              }
                            />
                          </div>
                        </div>
                        <ToggleRow
                          className='mt-3'
                          id={`queue-new-tab-${queueFile.id}`}
                          label='Open in new tab'
                          checked={queueFile.newTab !== false}
                          onChange={(e) =>
                            updateFileNewTab(queueFile.id, e.target.checked)
                          }
                        />
                        <div className='d-flex align-items-center justify-content-between mt-2'>
                          <small className='text-muted'>
                            <i className='fa fa-file' /> {queueFile.file.name} (
                            {(queueFile.file.size / 1024 / 1024).toFixed(2)} MB)
                          </small>
                        </div>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </div>
              </div>
            )}
          </div>
        ) : uploadMode === 'note' ? (
          /* Note Upload Form */
          <form
            id='add-note'
            onSubmit={(event) => {
              event.preventDefault()

              if (!noteTitle.trim()) {
                alert('Please enter a title for the note')
                return
              }

              if (!noteContent.trim()) {
                alert('Please enter some content for the note')
                return
              }

              if (
                enableLanguageFiles &&
                (!fileLanguages || !fileLanguages?.length)
              ) {
                alert('Please select a language!')
                return
              }

              setIsUploading(true)

              // Get thumbnail file if selected
              const nodes = event.target.elements
              const thumbInput = Array.from(nodes).find(
                (item) => item.type === 'file',
              )
              const thumbnail = thumbInput ? thumbInput.files[0] : null

              // Use uploadNote action but modify it to use 'text' instead of 'note_content'
              dispatch(
                uploadNote(
                  noteTitle.trim(),
                  noteContent,
                  thumbnail,
                  fileLanguages,
                ),
              )
                .then(() => {
                  setIsUploading(false)
                  setNoteTitle('')
                  setNoteContent('')

                  // Close modal after successful upload
                  setTimeout(() => {
                    props.history.push(
                      props.library.parentId
                        ? '/library/folder/' + props.library.parentId
                        : '/library',
                    )
                  }, 1000)
                })
                .catch((error) => {
                  setIsUploading(false)
                  console.error('Note upload error:', error)
                  alert('Failed to save note. Please try again.')
                })
            }}
          >
            <div className='row'>
              <div className='col-12'>
                <Input
                  type='text'
                  placeholder={
                    language.settings ? language.settings.title : 'Note Title'
                  }
                  value={noteTitle}
                  onChange={(event) => setNoteTitle(event.target.value)}
                  disabled={isUploading}
                />
              </div>
            </div>

            <div className='row mt-2'>
              <div className='col-12 d-flex align-items-center'>
                <ThumbnailInput
                  inputRef={noteThumbnailRef}
                  placeholder={
                    language.my_library
                      ? language.my_library.choose_thumbnail
                      : 'Choose thumbnail'
                  }
                />
              </div>
            </div>

            <div className='row'>
              {enableLanguageFiles ? (
                <div className='col-5'>
                  <label>Language</label>
                  <Select
                    options={[
                      { name: 'English', value: 1 },
                      { name: 'Spanish', value: 3 },
                    ]}
                    onChange={(selected) => {
                      setFileLanguages(selected)
                    }}
                    language={language}
                    placeholder={'Select Language'}
                    multi={true}
                    selectedOptions={fileLanguages}
                  />
                </div>
              ) : null}

              <div className='col-8 d-flex align-items-center my-4'>
                <div className='custom-control custom-radio mr-3'>
                  <input
                    className='custom-control-input mr-2'
                    type='radio'
                    name='accessible'
                    id='set-public-note'
                    checked={props.library.scope === 'public'}
                    onChange={() => dispatch(setPublic('public'))}
                    disabled={isUploading}
                  />
                  <label
                    className='custom-control-label'
                    htmlFor='set-public-note'
                  >
                    {language.my_library
                      ? language.my_library.public
                      : 'Public'}
                  </label>
                </div>

                <div className='custom-control custom-radio'>
                  <input
                    className='custom-control-input mr-2'
                    type='radio'
                    name='accessible'
                    id='set-private-note'
                    checked={props.library.scope === 'private'}
                    onChange={() => dispatch(setPublic('private'))}
                    disabled={isUploading}
                  />
                  <label
                    className='custom-control-label'
                    htmlFor='set-private-note'
                  >
                    {language.my_library
                      ? language.my_library.private
                      : 'Private'}
                  </label>
                </div>
              </div>

              {props.library.scope === 'private' ? (
                <div className='mar-top-20 mar-bot-20 col-4'>
                  <Search
                    placeholder={
                      language.general ? language.general.search : 'Search'
                    }
                    value={props.assignSearchValue}
                    onChange={(event) =>
                      dispatch(setAssignSearchValue(event.target.value))
                    }
                    disabled={isUploading}
                  />
                </div>
              ) : null}

              <div className='col-5'>
                <label>Move to</label>
                <Select
                  options={[{ name: 'Library', id: null }, ...selectOptions]}
                  onChange={(e) => {
                    setActiveFolder(e.option.name)
                    handleFolderChange(e)
                  }}
                  language={language}
                  placeholder={activeFolder}
                  multi={false}
                  selectedOption={selectedFolder}
                  disabled={isUploading}
                />
              </div>
            </div>

            {props.library.scope === 'private' && (
              <div>
                <div className='d-flex mt-3 mb-2'>
                  <Nav
                    className='nav-fill flex-column flex-sm-row col-12 p-2 rounded'
                    id='tabs-text'
                    style={{ backgroundColor: '#f6f9fc' }}
                    pills
                    role='tablist'
                  >
                    <NavItem className='nav-items'>
                      <NavLink
                        aria-selected={props.library.currentTab === 'category'}
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                          props.library.currentTab === 'category'
                            ? 'active-item active-link'
                            : 'nav-item'
                        }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'category',
                              'selectedOrgGroups',
                              'groupOrgs',
                              'toggleOrgGroups',
                              'showSelectedOrgGroups',
                              'setShowSelectedOrgGroups',
                              'setHideSelectedOrgGroups',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                      >
                        {language.general
                          ? language.general.category
                          : 'Category'}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        aria-selected={props.library.currentTab === 'user'}
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                          props.library.currentTab === 'user'
                            ? 'active-item active-link'
                            : 'nav-item'
                        }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'user',
                              'selectedUsers',
                              'users',
                              'toggleUsers',
                              'showSelectedUsers',
                              'setShowSelectedUsers',
                              'setHideSelectedUsers',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                        color='none'
                      >
                        {language.general ? language.general.users : 'Users'}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        aria-selected={
                          props.library.currentTab === 'location-group'
                        }
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                          props.library.currentTab === 'location-group'
                            ? 'active-item active-link'
                            : 'nav-item'
                        }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'location-group',
                              'selectedLocationGroups',
                              'locationGroups',
                              'toggleLocationGroups',
                              'showSelectedLocationGroups',
                              'setShowSelectedLocationGroups',
                              'setHideSelectedLocationGroups',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                      >
                        {language.general
                          ? language.general.location_categories
                          : 'Location Groups'}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        aria-selected={props.library.currentTab === 'stores'}
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                          props.library.currentTab === 'stores'
                            ? 'active-item active-link'
                            : 'nav-item'
                        }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'stores',
                              'selectedStores',
                              'stores',
                              'toggleStores',
                              'showSelectedStores',
                              'setShowSelectedStores',
                              'setHideSelectedStores',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                      >
                        {language.settings
                          ? language.settings.location
                          : 'Location'}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        aria-selected={props.library.currentTab === 'job-title'}
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold ${
                          props.library.currentTab === 'job-title'
                            ? 'active-item active-link'
                            : 'nav-item'
                        }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'job-title',
                              'selectedRoles',
                              'roles',
                              'toggleRoles',
                              'showSelectedRoles',
                              'setShowSelectedRoles',
                              'setHideSelectedRoles',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                      >
                        {language.settings
                          ? language.settings.assign_job_title
                          : 'Job Title'}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>

                <AssignTabs
                  items={props[props.library.currentItems]}
                  toggleItem={props[props.library.currentToggleFunction]}
                  selectedItems={props.library.currentSelectedItems}
                  mainProps={props.library}
                  assignSearchValue={props.library.assignSearchValue}
                  setHideSelectedItems={
                    props[props.library.currentHideSelectedItemsFunction]
                  }
                  setShowSelectedItems={
                    props[props.library.currentShowSelectedItemsFunction]
                  }
                  showSelectedItems={
                    props.library[props.library.currentShowSelectedItems]
                  }
                  language={props.language}
                  resetSelectedItems={() => dispatch(resetSelectedItems())}
                  setCurrentTab={setCurrentTab}
                  setAssignSearchValue={(event) =>
                    dispatch(setAssignSearchValue(event.target.value))
                  }
                  setPublic={(val) => dispatch(setPublic(val))}
                  enableCategories={enableCategories}
                />
              </div>
            )}

            {/* Note Content Editor */}
            <div className='row mt-4'>
              <div className='col-12'>
                <label>Note Content</label>
                <TextEditor
                  customStyle='px-2'
                  textContent={noteContent}
                  onChangeText={(newContent) => setNoteContent(newContent)}
                  setOptions={{
                    buttonList: [
                      [
                        'undo',
                        'redo',
                        'font',
                        'fontSize',
                        'formatBlock',
                        'list',
                        'align',
                        'fontColor',
                      ],
                      ['bold', 'underline', 'italic', 'horizontalRule'],
                      ['codeView', 'outdent', 'indent'],
                      ['table', 'image', 'link', 'video'],
                    ],
                  }}
                />
              </div>
            </div>
          </form>
        ) : (
          /* Single File Upload Form (Existing) */
          <form
            id='add-file'
            onSubmit={(event) => {
              if (
                enableLanguageFiles &&
                (!fileLanguages || !fileLanguages?.length)
              ) {
                event.preventDefault()
                alert('Please select a language!')
                return
              }

              // Dispatch upload and handle success
              dispatch(upload(event, fileLanguages))
                .then(() => {
                  // Close modal after successful single file upload
                  setTimeout(() => {
                    props.history.push(
                      props.library.parentId
                        ? '/library/folder/' + props.library.parentId
                        : '/library',
                    )
                  }, 1000)
                })
                .catch((error) => {
                  console.error('Single file upload error:', error)
                })
            }}
          >
            <div className='row'>
              <div className='col-12'>
                <Input
                  type='text'
                  placeholder={
                    language.settings ? language.settings.title : null
                  }
                />
              </div>
            </div>
            {props.chooseFile === false && (
              <div className='row mt-2'>
                <div className='col-12'>
                  <Input type='text' placeholder={props.inputPlaceHolder} />
                </div>
              </div>
            )}
            {props.chooseFile !== false && (
              <div className='row mt-2'>
                <div className='col-6'>
                  <File
                    language={language}
                    placeholder={
                      language.general ? language.general.choose_file : null
                    }
                  />
                </div>
              </div>
            )}

            <div className='row mt-2'>
              <div className='col-12 d-flex align-items-center'>
                <ThumbnailInput
                  inputRef={singleThumbnailRef}
                  placeholder={
                    language.my_library
                      ? language.my_library.choose_thumbnail
                      : 'Choose thumbnail'
                  }
                />
              </div>
            </div>

            <div className='row mt-3'>
              <div className='col-12'>
                <ToggleRow
                  id='upload-new-tab'
                  name='new_tab'
                  label='Open in new tab'
                  defaultChecked
                />
              </div>
            </div>

            <div className='row'>
              {enableLanguageFiles ? (
                <div className='col-5'>
                  <label>Language</label>
                  <Select
                    options={[
                      { name: 'English', value: 1 },
                      { name: 'Spanish', value: 3 },
                    ]}
                    onChange={(selected) => {
                      setFileLanguages(selected)
                    }}
                    language={language}
                    placeholder={'Select Language'}
                    multi={true}
                    selectedOptions={fileLanguages}
                  />
                </div>
              ) : null}

              <div className='col-8 d-flex align-items-center my-4'>
                <div className='custom-control custom-radio mr-3'>
                  <input
                    className='custom-control-input mr-2'
                    type='radio'
                    name='accessible'
                    id='set-public'
                    checked={props.library.scope === 'public'}
                    onChange={() => dispatch(setPublic('public'))}
                  />
                  <label className='custom-control-label' htmlFor='set-public'>
                    {language.my_library ? language.my_library.public : null}
                  </label>
                </div>

                <div className='custom-control custom-radio'>
                  <input
                    className='custom-control-input mr-2'
                    type='radio'
                    name='accessible'
                    id='set-private'
                    checked={props.library.scope === 'private'}
                    onChange={() => dispatch(setPublic('private'))}
                  />
                  <label className='custom-control-label' htmlFor='set-private'>
                    {language.my_library ? language.my_library.private : null}
                  </label>
                </div>
              </div>

              {props.library.scope === 'private' ? (
                <div className='mar-top-20 mar-bot-20 col-4'>
                  <Search
                    placeholder={
                      language.general ? language.general.search : null
                    }
                    value={props.assignSearchValue}
                    onChange={(event) =>
                      dispatch(setAssignSearchValue(event.target.value))
                    }
                  />
                </div>
              ) : null}

              <div className='col-5'>
                <label>Move to</label>
                <Select
                  options={[{ name: 'Library', id: null }, ...selectOptions]}
                  onChange={(e) => {
                    setActiveFolder(e.option.name)
                    handleFolderChange(e)
                  }}
                  language={language}
                  placeholder={activeFolder}
                  multi={false}
                  selectedOption={selectedFolder}
                />
              </div>
            </div>

            {props.library.scope === 'private' && (
              <div>
                <div className='d-flex mt-3 mb-2'>
                  <Nav
                    className='nav-fill flex-column flex-sm-row col-12 p-2 rounded'
                    id='tabs-text'
                    style={{ backgroundColor: '#f6f9fc' }}
                    pills
                    role='tablist'
                  >
                    <NavItem className='nav-items'>
                      <NavLink
                        aria-selected={props.library.currentTab === 'category'}
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold 
                          ${
                            props.library.currentTab === 'category'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'category',
                              'selectedOrgGroups',
                              'groupOrgs',
                              'toggleOrgGroups',
                              'showSelectedOrgGroups',
                              'setShowSelectedOrgGroups',
                              'setHideSelectedOrgGroups',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                      >
                        {language.general
                          ? language.general.category
                          : 'Category'}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        aria-selected={props.library.currentTab === 'user'}
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold 
                          ${
                            props.library.currentTab === 'user'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'user',
                              'selectedUsers',
                              'users',
                              'toggleUsers',
                              'showSelectedUsers',
                              'setShowSelectedUsers',
                              'setHideSelectedUsers',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                        color='none'
                      >
                        {language.general ? language.general.users : 'Users'}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        aria-selected={
                          props.library.currentTab === 'location-group'
                        }
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold 
                          ${
                            props.library.currentTab === 'location-group'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'location-group',
                              'selectedLocationGroups',
                              'locationGroups',
                              'toggleLocationGroups',
                              'showSelectedLocationGroups',
                              'setShowSelectedLocationGroups',
                              'setHideSelectedLocationGroups',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                      >
                        {language.general
                          ? language.general.location_categories
                          : 'Location Groups'}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        aria-selected={props.library.currentTab === 'stores'}
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold 
                          ${
                            props.library.currentTab === 'stores'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'stores',
                              'selectedStores',
                              'stores',
                              'toggleStores',
                              'showSelectedStores',
                              'setShowSelectedStores',
                              'setHideSelectedStores',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                      >
                        {language.settings
                          ? language.settings.location
                          : 'Location'}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        aria-selected={props.library.currentTab === 'job-title'}
                        className={`mb-sm-3 mb-md-0 py-2 bg-transparent font-weight-bold 
                          ${
                            props.library.currentTab === 'job-title'
                              ? 'active-item active-link'
                              : 'nav-item'
                          }`}
                        onClick={() =>
                          dispatch(
                            setCurrentTab(
                              'job-title',
                              'selectedRoles',
                              'roles',
                              'toggleRoles',
                              'showSelectedRoles',
                              'setShowSelectedRoles',
                              'setHideSelectedRoles',
                            ),
                          )
                        }
                        href='#pablo'
                        role='tab'
                      >
                        {language.settings
                          ? language.settings.assign_job_title
                          : 'Job Title'}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>

                <AssignTabs
                  items={props[props.library.currentItems]}
                  toggleItem={props[props.library.currentToggleFunction]}
                  selectedItems={props.library.currentSelectedItems}
                  mainProps={props.library}
                  assignSearchValue={props.library.assignSearchValue}
                  setHideSelectedItems={
                    props[props.library.currentHideSelectedItemsFunction]
                  }
                  setShowSelectedItems={
                    props[props.library.currentShowSelectedItemsFunction]
                  }
                  showSelectedItems={
                    props.library[props.library.currentShowSelectedItems]
                  }
                  language={props.language}
                  resetSelectedItems={() => dispatch(resetSelectedItems())}
                  setCurrentTab={setCurrentTab}
                  setAssignSearchValue={(event) =>
                    dispatch(setAssignSearchValue(event.target.value))
                  }
                  setPublic={(val) => dispatch(setPublic(val))}
                  enableCategories={enableCategories}
                />
              </div>
            )}
          </form>
        )}
      </ModalBody>

      <ModalFooter className='py-3 border-top'>
        {uploadMode === 'multiple' ? (
          <div className='d-flex align-items-center w-100 justify-content-between'>
            <div className='d-flex align-items-center'>
              {fileQueue.length > 0 && (
                <span className='text-muted small mr-3'>
                  {fileQueue.length} file(s) selected
                </span>
              )}
            </div>
            <div>
              <Button
                color='info'
                onClick={handleMultipleUpload}
                disabled={isUploading || fileQueue.length === 0}
              >
                {isUploading ? (
                  <>
                    <i className='fa fa-spinner fa-spin mr-2' />
                    Uploading {fileQueue.length} files...
                  </>
                ) : (
                  <>
                    <i className='fa fa-upload mr-2' />
                    Upload All Files
                  </>
                )}
              </Button>
              <Link
                to={
                  props.library.parentId
                    ? '/library/folder/' + props.library.parentId
                    : '/library'
                }
                className='btn py-2 btn-transparent active-link ml-2'
              >
                {language.general ? language.general.cancel : null}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <ProgressiveButton
              percent={props.library.libraryUploadPercent}
              status={props.library.fileUploadStatus}
              placeholder={
                uploadMode === 'note'
                  ? isUploading
                    ? 'Saving Note...'
                    : 'Save Note'
                  : language.general
                    ? language.general.submit
                    : null
              }
              language={props.language}
              form={uploadMode === 'note' ? 'add-note' : 'add-file'}
              type='submit'
            />
            <Link
              to={
                props.library.parentId
                  ? '/library/folder/' + props.library.parentId
                  : '/library'
              }
              className='btn py-2 btn-transparent active-link'
            >
              {language.general ? language.general.cancel : null}
            </Link>
          </>
        )}
      </ModalFooter>
    </CustomModal>
  )
}

export default Upload
